import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Building2, 
  MapPin, 
  Award, 
  ExternalLink, 
  CheckCircle2, 
  ArrowRight, 
  HelpCircle, 
  Activity, 
  PhoneCall, 
  FileText, 
  ShieldCheck, 
  DollarSign,
  ChevronRight,
  Sparkles
} from 'lucide-react';
import MetaTags from '@/components/SEO/MetaTags';
import { getUSStateOrMetro, US_STATES } from '@/data/usStatesData';
import { SITE_ORIGIN } from '@/config/site';

export default function USStatePage() {
  const { stateSlug } = useParams();
  const state = getUSStateOrMetro(stateSlug);

  if (!state) {
    return <Navigate to="/cancer-centers-by-state" replace />;
  }

  const isCity = Boolean(state.cityHighlight);
  const locationName = isCity ? `${state.cityHighlight}, ${state.name}` : `${state.name} (${state.abbr})`;
  const pageUrl = `${SITE_ORIGIN}/cancer-centers/${stateSlug.toLowerCase()}`;

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'MedicalWebPage',
      name: `Best Cancer Centers in ${locationName} | Top Hospitals & Specialists`,
      description: `Guide to top NCI-designated cancer centers, hospital networks, clinical trials, and oncology navigation in ${locationName}.`,
      url: pageUrl,
      about: {
        '@type': 'MedicalCondition',
        name: 'Cancer',
      },
      provider: {
        '@type': 'MedicalOrganization',
        name: 'ByOnco Care Navigation',
        url: SITE_ORIGIN,
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: isCity ? [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_ORIGIN },
        { '@type': 'ListItem', position: 2, name: 'US Directory', item: `${SITE_ORIGIN}/cancer-centers-by-state` },
        { '@type': 'ListItem', position: 3, name: state.name, item: `${SITE_ORIGIN}/cancer-centers/${state.slug}` },
        { '@type': 'ListItem', position: 4, name: state.cityHighlight, item: pageUrl },
      ] : [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_ORIGIN },
        { '@type': 'ListItem', position: 2, name: 'US Directory', item: `${SITE_ORIGIN}/cancer-centers-by-state` },
        { '@type': 'ListItem', position: 3, name: state.name, item: pageUrl },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: state.faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: f.a,
        },
      })),
    },
  ];

  return (
    <div className="min-h-screen bg-cream-paper text-forest-ink pt-20 pb-24">
      <MetaTags
        title={`Best Cancer Centers in ${locationName} | Top Hospitals & Oncologists`}
        description={`Compare top cancer centers, NCI-designated hospitals, clinical trials, and oncologists in ${locationName}. Get matched and book second opinions with ByOnco.`}
        path={`/cancer-centers/${stateSlug.toLowerCase()}`}
        jsonLd={jsonLd}
      />

      {/* Breadcrumb */}
      <nav className="max-w-6xl mx-auto px-6 sm:px-12 py-4 text-xs text-forest-ink/60 flex items-center gap-2">
        <Link to="/" className="hover:text-emerald-700">Home</Link>
        <ChevronRight className="w-3 h-3" />
        <Link to="/cancer-centers-by-state" className="hover:text-emerald-700">US Directory</Link>
        <ChevronRight className="w-3 h-3" />
        {isCity ? (
          <>
            <Link to={`/cancer-centers/${state.slug}`} className="hover:text-emerald-700">{state.name}</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-forest-ink font-semibold">{state.cityHighlight}</span>
          </>
        ) : (
          <span className="text-forest-ink font-semibold">{state.name}</span>
        )}
      </nav>

      {/* Hero Header */}
      <section className="relative pt-10 pb-14 bg-gradient-to-b from-emerald-50/70 via-cream-paper to-cream-paper border-b border-border-mist">
        <div className="max-w-6xl mx-auto px-6 sm:px-12">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-100 text-emerald-900 text-xs sm:text-sm font-medium mb-5 border border-emerald-200/60 shadow-2xs">
            <MapPin className="w-3.5 h-3.5 text-emerald-700" />
            <span>
              {isCity ? `${state.cityHighlight} Metro · ${state.region} Region` : `${state.region} Region · ${state.capital} (Capital)`}
            </span>
          </div>

          <h1 className="text-3xl sm:text-5xl font-bold font-serif text-forest-ink tracking-tight mb-4 leading-tight">
            Best Cancer Centers in <span className="text-emerald-700">{locationName}</span>
          </h1>

          <p className="text-base sm:text-lg text-charcoal/85 max-w-3xl mb-8 leading-relaxed font-sans">
            {isCity
              ? `Comprehensive guide to top cancer centers, NCI-designated hospital networks, and clinical trials serving patients in ${state.cityHighlight} and throughout ${state.name}. ${state.highlights}`
              : `${state.highlights} Learn about top NCI institutions, leading hospital networks, clinical trials, and patient navigation in ${state.name}.`}
          </p>

          <div className="flex flex-wrap items-center gap-3">
            <Link
              to="/get-matched"
              className="px-6 py-3.5 rounded-xl bg-forest-ink hover:bg-forest-shadow text-white font-bold text-sm transition-all shadow-md hover:shadow-lg inline-flex items-center gap-2"
            >
              Get Matched in {isCity ? state.cityHighlight : state.name}
            </Link>
            <Link
              to="/cost-calculator"
              className="px-6 py-3.5 rounded-xl bg-white hover:bg-emerald-50 text-forest-ink border border-border-mist font-semibold text-sm transition-all shadow-xs"
            >
              Calculate Treatment Cost
            </Link>
            <Link
              to="/talk"
              className="px-5 py-3.5 rounded-xl bg-mist hover:bg-emerald-100/60 text-forest-ink font-semibold text-sm transition-all inline-flex items-center gap-2"
            >
              <Sparkles className="w-4 h-4 text-emerald-700" />
              Ask ByOnco AI
            </Link>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-6 sm:px-12 py-12 grid grid-cols-1 lg:grid-cols-3 gap-10">
        {/* Main Content Area */}
        <div className="lg:col-span-2 space-y-12">
          
          {/* NCI-Designated Centers Section */}
          <section>
            <div className="flex items-center gap-3 mb-6">
              <Award className="w-6 h-6 text-emerald-600" />
              <h2 className="text-2xl font-bold font-serif text-forest-ink">
                {state.nciCenters.length > 0
                  ? `NCI-Designated Cancer Centers in ${state.name}`
                  : `Leading Cancer Treatment Centers in ${state.name}`}
              </h2>
            </div>

            {state.nciCenters.length > 0 ? (
              <div className="space-y-6">
                {state.nciCenters.map((center, idx) => (
                  <div
                    key={idx}
                    className="bg-white rounded-2xl p-6 border border-forest-ink/10 shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <div>
                        <h3 className="text-xl font-bold text-forest-ink">{center.name}</h3>
                        <p className="text-xs text-forest-ink/60 flex items-center gap-1 mt-1">
                          <MapPin className="w-3.5 h-3.5 text-emerald-600" />
                          {center.city}, {state.name}
                        </p>
                      </div>
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 whitespace-nowrap">
                        {center.designation}
                      </span>
                    </div>

                    <div className="my-4">
                      <div className="text-xs font-semibold text-forest-ink/80 mb-2 uppercase tracking-wider">
                        Key Specialties & Innovations:
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {center.specialties.map((spec, sidx) => (
                          <span
                            key={sidx}
                            className="px-2.5 py-1 rounded-lg text-xs bg-forest-ink/5 text-forest-ink/80 font-medium"
                          >
                            {spec}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="pt-4 border-t border-forest-ink/5 flex items-center justify-between">
                      <a
                        href={center.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:underline"
                      >
                        Official Center Website <ExternalLink className="w-3 h-3" />
                      </a>
                      <Link
                        to={`/get-matched?state=${state.abbr}`}
                        className="text-xs font-bold text-forest-ink bg-cream-paper px-3 py-1.5 rounded-lg hover:bg-emerald-100 transition-colors"
                      >
                        Request Specialist Referral
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-2xl p-6 border border-forest-ink/10 text-sm text-forest-ink/80 leading-relaxed">
                <p className="mb-4">
                  While {state.name} does not have a freestanding NCI-designated facility within state borders, residents access premier care through the following accredited health systems and formal referral alliances:
                </p>
                <ul className="space-y-2 mb-4">
                  {state.topNetworks.map((net, nidx) => (
                    <li key={nidx} className="flex items-center gap-2 font-medium">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                      <span>{net}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-forest-ink/60">
                  ByOnco coordinates seamless virtual consultations and cross-state referrals with neighboring NCI Comprehensive Cancer Centers.
                </p>
              </div>
            )}
          </section>

          {/* Major Hospital Networks */}
          {state.nciCenters.length > 0 && (
            <section>
              <h3 className="text-xl font-bold font-serif text-forest-ink mb-4 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-forest-ink/70" />
                Additional Major Hospital Networks in {state.name}
              </h3>
              <div className="bg-white rounded-2xl p-6 border border-forest-ink/10">
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {state.topNetworks.map((net, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-forest-ink/80">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                      <span>{net}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          )}

          {/* Clinical Trials Overview */}
          <section className="bg-white rounded-2xl p-6 border border-forest-ink/10">
            <h3 className="text-xl font-bold font-serif text-forest-ink mb-3 flex items-center gap-2">
              <Activity className="w-5 h-5 text-emerald-600" />
              Clinical Trials & Precision Research in {state.name}
            </h3>
            <p className="text-sm text-forest-ink/80 leading-relaxed mb-4">
              {state.clinicalTrials}
            </p>
            <div className="p-4 rounded-xl bg-forest-ink/5 text-xs text-forest-ink/80 flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" />
              <div>
                <strong>Need trial matching?</strong> ByOnco scans active National Cancer Institute (NCI) and ClinicalTrials.gov registers for your exact biomarker profile.
              </div>
            </div>
          </section>

          {/* FAQs Section */}
          <section>
            <h3 className="text-2xl font-bold font-serif text-forest-ink mb-6 flex items-center gap-2">
              <HelpCircle className="w-6 h-6 text-emerald-600" />
              Frequently Asked Questions for {state.name}
            </h3>
            <div className="space-y-4">
              {state.faqs.map((faq, idx) => (
                <div key={idx} className="bg-white rounded-xl p-5 border border-forest-ink/10">
                  <h4 className="font-bold text-base text-forest-ink mb-2">{faq.q}</h4>
                  <p className="text-sm text-forest-ink/75 leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* Sidebar Navigation & Help */}
        <div className="space-y-6">
          {/* Quick Actions Card */}
          <div className="bg-white rounded-2xl p-6 border border-emerald-200/80 shadow-sm relative overflow-hidden">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-emerald-800 mb-2">
              <Sparkles className="w-4 h-4 text-emerald-600" />
              <span>Oncology Navigation</span>
            </div>
            <h4 className="text-lg font-bold font-serif text-forest-ink mb-2">Need Help in {state.name}?</h4>
            <p className="text-xs text-charcoal/80 mb-5 leading-relaxed">
              Connect with our clinical navigation team to verify center in-network status, organize second opinions, and review clinical trials.
            </p>
            <div className="space-y-2.5">
              <Link
                to={`/get-matched?state=${state.abbr}`}
                className="w-full block py-2.5 px-4 text-center rounded-xl bg-forest-ink hover:bg-forest-shadow text-white font-bold text-sm transition-all shadow-sm"
              >
                Find Specialists in {state.abbr}
              </Link>
              <Link
                to="/talk"
                className="w-full block py-2.5 px-4 text-center rounded-xl bg-mist hover:bg-emerald-50 text-forest-ink border border-border-mist font-semibold text-sm transition-all"
              >
                Ask ByOnco AI (24/7)
              </Link>
              <Link
                to="/second-opinion"
                className="w-full block py-2.5 px-4 text-center rounded-xl bg-white hover:bg-emerald-50 text-forest-ink border border-border-mist font-semibold text-sm transition-all"
              >
                Get Virtual Second Opinion
              </Link>
            </div>
          </div>

          {/* Major Cities Covered */}
          <div className="bg-white rounded-2xl p-6 border border-forest-ink/10">
            <h4 className="text-sm font-bold uppercase tracking-wider text-forest-ink/70 mb-3">
              Major Cities in {state.name}
            </h4>
            <div className="flex flex-wrap gap-2">
              {state.majorCities.map((city, cidx) => (
                <span
                  key={cidx}
                  className="px-2.5 py-1 rounded-md bg-cream-paper text-forest-ink text-xs font-medium border border-forest-ink/10"
                >
                  {city}
                </span>
              ))}
            </div>
          </div>

          {/* Cancer Types Directory Link */}
          <div className="bg-white rounded-2xl p-6 border border-forest-ink/10">
            <h4 className="text-sm font-bold uppercase tracking-wider text-forest-ink/70 mb-3">
              Cancer Guides & Treatment
            </h4>
            <ul className="space-y-2 text-xs">
              {[
                { name: 'Breast Cancer', slug: 'breast' },
                { name: 'Lung Cancer', slug: 'lung' },
                { name: 'Prostate Cancer', slug: 'prostate' },
                { name: 'Colorectal Cancer', slug: 'colorectal' },
                { name: 'Pancreatic Cancer', slug: 'pancreatic' },
              ].map((c) => (
                <li key={c.slug}>
                  <Link
                    to={`/cancer/${c.slug}`}
                    className="text-emerald-700 hover:underline flex items-center justify-between"
                  >
                    <span>{c.name} Guide</span>
                    <ChevronRight className="w-3.5 h-3.5" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Key US States & Metros Navigation */}
          <div className="bg-white rounded-2xl p-6 border border-forest-ink/10">
            <h4 className="text-sm font-bold uppercase tracking-wider text-forest-ink/70 mb-3">
              Explore Top US Oncology Hubs
            </h4>
            <div className="flex flex-wrap gap-1.5 text-xs">
              {[
                { name: 'California', slug: 'california' },
                { name: 'Texas', slug: 'texas' },
                { name: 'New York', slug: 'new-york' },
                { name: 'Massachusetts (Boston)', slug: 'massachusetts' },
                { name: 'Pennsylvania', slug: 'pennsylvania' },
                { name: 'Ohio (Cleveland)', slug: 'ohio' },
                { name: 'Indiana', slug: 'indiana' },
                { name: 'Georgia (Atlanta)', slug: 'georgia' },
                { name: 'Washington (Seattle)', slug: 'washington' },
                { name: 'North Carolina', slug: 'north-carolina' },
                { name: 'South Carolina', slug: 'south-carolina' },
                { name: 'North Dakota', slug: 'north-dakota' },
                { name: 'South Dakota', slug: 'south-dakota' },
                { name: 'Oregon', slug: 'oregon' },
                { name: 'Maryland', slug: 'maryland' },
              ].map((s) => (
                <Link
                  key={s.slug}
                  to={`/cancer-centers/${s.slug}`}
                  className="px-2 py-1 rounded bg-forest-ink/5 hover:bg-emerald-100 hover:text-emerald-900 transition-colors"
                >
                  {s.name}
                </Link>
              ))}
            </div>
            <div className="mt-4 pt-3 border-t border-forest-ink/10 flex flex-col gap-2 text-xs">
              <Link to="/cancer-centers-by-state" className="font-semibold text-emerald-700 hover:underline flex items-center justify-between">
                <span>All 50 States Directory</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </Link>
              <Link to="/best-cancer-navigation-platform" className="text-forest-ink/80 hover:text-emerald-700 flex items-center justify-between">
                <span>Best Cancer Navigation Platform</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </Link>
              <Link to="/best-cancer-app" className="text-forest-ink/80 hover:text-emerald-700 flex items-center justify-between">
                <span>#1 Cancer App & AI Agent</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </Link>
              <Link to="/best-medical-tourism-oncology" className="text-forest-ink/80 hover:text-emerald-700 flex items-center justify-between">
                <span>Leading Medical Tourism Platform</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
